import { DebugWidget, createWidgetContainer } from './DebugOverlay';
import workerResponseQueue from '../gemoetries/PPCPointCloudGeometry/WorkerResponseQueue';

// Tile timing data structure
export interface TileOperationTiming {
  op: string;
  ms: number;
}

export interface TileTimingEntry {
  numPoints: number;
  totalMs: number;
  timings: TileOperationTiming[];
  timestamp: number;
}

// Expose widget class globally for tracking
declare global {
  interface Window {
    PPCLoaderDebugWidget?: typeof PPCLoaderDebugWidget;
  }
}

/**
 * PPC Loader Performance Profiling Widget
 * Tracks worker activity, geometry updates, and load times
 */
export class PPCLoaderDebugWidget implements DebugWidget {
  name = 'ppc-loader-debug';
  container: HTMLElement;
  private viewer: unknown;

  // Stats elements
  private activeWorkersEl: HTMLElement;
  private queuedUpdatesEl: HTMLElement;
  private avgLoadTimeEl: HTMLElement;
  private lastLoadTimeEl: HTMLElement;
  private tilesLoadingEl: HTMLElement;
  private tilesLoadedEl: HTMLElement;
  private workerCallbacksEl: HTMLElement;
  private blockTimeEl: HTMLElement;
  private lastBlockEl: HTMLElement;
  private maxBlockEl: HTMLElement;
  private opStatsEl: HTMLElement;
  private slowTileEl: HTMLElement;
  private slowTileInfoEl: HTMLElement;
  private hierarchyQueueEl: HTMLElement;
  private hierarchyTimeEl: HTMLElement;

  // Canvas for timeline
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  // Tracking data
  private static activeWorkers = 0;
  private static tilesLoading = 0;
  private static tilesLoaded = 0;
  private static loadTimes: number[] = [];
  private static workerCallbacksThisFrame = 0;
  private static maxLoadTimes = 50;

  // Thread blocking tracking
  private static blockTimes: number[] = [];
  private static lastBlockTime = 0;
  private static maxBlockTime = 0;
  private static maxBlockTimes = 60;

  // Hierarchy loading tracking
  private static hierarchyLoadTimes: number[] = [];
  private static lastHierarchyTime = 0;
  private static maxHierarchyTime = 0;
  private static hierarchyLoadsThisFrame = 0;

  // Timeline data for visualizing spikes
  private static frameEvents: Array<{ timestamp: number; workers: number; callbacks: number; blockTime: number }> = [];
  private static maxFrameHistory = 100;

  // Tile operation timing tracking
  private static tileTimings: TileTimingEntry[] = [];
  private static maxTileTimings = 50;
  private static lastSlowTile: TileTimingEntry | null = null;
  // Aggregated operation times across all recent tiles
  private static operationTotals: Map<string, { total: number; count: number }> = new Map();

  constructor(viewer: unknown) {
    this.viewer = viewer;
    this.container = createWidgetContainer('⚡ PPC Loader Profile');

    const content = document.createElement('div');
    content.style.cssText = 'line-height: 1.6;';
    content.innerHTML = `
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 4px; font-size: 11px; margin-bottom: 8px;">
        <div><strong>Active Workers:</strong> <span id="ppc-active-workers" style="color: #4CAF50;">--</span></div>
        <div><strong>Response Queue:</strong> <span id="ppc-queued-updates" style="color: #FF9800;">--</span></div>
        <div><strong>Tiles Loading:</strong> <span id="ppc-tiles-loading" style="color: #2196F3;">--</span></div>
        <div><strong>Tiles Loaded:</strong> <span id="ppc-tiles-loaded" style="color: #9C27B0;">--</span></div>
        <div><strong>Avg Load Time:</strong> <span id="ppc-avg-load">--</span></div>
        <div><strong>Last Load:</strong> <span id="ppc-last-load">--</span></div>
        <div><strong>Callbacks/Frame:</strong> <span id="ppc-callbacks" style="color: #f44336;">--</span></div>
        <div><strong>Avg Block:</strong> <span id="ppc-block-time">--</span></div>
        <div><strong>Last Block:</strong> <span id="ppc-last-block">--</span></div>
        <div><strong>Max Block:</strong> <span id="ppc-max-block" style="color: #f44336;">--</span></div>
        <div><strong>Hierarchy Queue:</strong> <span id="ppc-hierarchy-queue" style="color: #00BCD4;">--</span></div>
        <div><strong>Hierarchy Time:</strong> <span id="ppc-hierarchy-max" style="color: #00BCD4;">--</span></div>
      </div>
      <div id="ppc-tile-ops" style="font-size: 9px; color: rgba(255,255,255,0.7); margin-bottom: 6px; padding: 4px; background: rgba(0,0,0,0.2); border-radius: 3px;">
        <div style="margin-bottom: 2px; color: rgba(255,255,255,0.5);">Tile Ops (avg ms):</div>
        <span id="ppc-op-stats">--</span>
      </div>
      <div id="ppc-slow-tile" style="font-size: 9px; color: #ff6b6b; margin-bottom: 6px; display: none;">
        <strong>Slowest Tile:</strong> <span id="ppc-slow-tile-info">--</span>
      </div>
      <div style="font-size: 10px; color: rgba(255,255,255,0.6); margin-bottom: 4px;">Activity Timeline (100 frames)</div>
    `;

    this.container.appendChild(content);

    // Get stat elements - these are all created in the innerHTML above
    const q = (id: string): HTMLElement => content.querySelector(id) ?? document.createElement('span');
    this.activeWorkersEl = q('#ppc-active-workers');
    this.queuedUpdatesEl = q('#ppc-queued-updates');
    this.avgLoadTimeEl = q('#ppc-avg-load');
    this.lastLoadTimeEl = q('#ppc-last-load');
    this.tilesLoadingEl = q('#ppc-tiles-loading');
    this.tilesLoadedEl = q('#ppc-tiles-loaded');
    this.workerCallbacksEl = q('#ppc-callbacks');
    this.blockTimeEl = q('#ppc-block-time');
    this.lastBlockEl = q('#ppc-last-block');
    this.maxBlockEl = q('#ppc-max-block');
    this.opStatsEl = q('#ppc-op-stats');
    this.slowTileEl = q('#ppc-slow-tile');
    this.slowTileInfoEl = q('#ppc-slow-tile-info');
    this.hierarchyQueueEl = q('#ppc-hierarchy-queue');
    this.hierarchyTimeEl = q('#ppc-hierarchy-max');

    // Create timeline canvas
    this.canvas = document.createElement('canvas');
    this.canvas.width = 360;
    this.canvas.height = 60;
    this.canvas.style.cssText = `
      width: 100%;
      height: 60px;
      background: rgba(0, 0, 0, 0.3);
      border-radius: 4px;
    `;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to get 2D canvas context');
    this.ctx = ctx;
    this.container.appendChild(this.canvas);
  }

  update(): void {
    // Update stats
    this.activeWorkersEl.textContent = PPCLoaderDebugWidget.activeWorkers.toString();
    this.queuedUpdatesEl.textContent = workerResponseQueue.getQueueSize().toString();
    this.tilesLoadingEl.textContent = PPCLoaderDebugWidget.tilesLoading.toString();
    this.tilesLoadedEl.textContent = PPCLoaderDebugWidget.tilesLoaded.toString();
    this.workerCallbacksEl.textContent = PPCLoaderDebugWidget.workerCallbacksThisFrame.toString();

    // Calculate average load time
    if (PPCLoaderDebugWidget.loadTimes.length > 0) {
      const avg = PPCLoaderDebugWidget.loadTimes.reduce((a, b) => a + b, 0) / PPCLoaderDebugWidget.loadTimes.length;
      this.avgLoadTimeEl.textContent = `${avg.toFixed(0)}ms`;

      const last = PPCLoaderDebugWidget.loadTimes[PPCLoaderDebugWidget.loadTimes.length - 1];
      this.lastLoadTimeEl.textContent = `${last.toFixed(0)}ms`;

      // Color code based on time
      this.lastLoadTimeEl.style.color = last > 100 ? '#f44336' : last > 50 ? '#FFC107' : '#4CAF50';
    } else {
      this.avgLoadTimeEl.textContent = '--';
      this.lastLoadTimeEl.textContent = '--';
    }

    // Calculate block times
    if (PPCLoaderDebugWidget.blockTimes.length > 0) {
      const avg = PPCLoaderDebugWidget.blockTimes.reduce((a, b) => a + b, 0) / PPCLoaderDebugWidget.blockTimes.length;
      this.blockTimeEl.textContent = `${avg.toFixed(1)}ms`;
      this.blockTimeEl.style.color = avg > 16 ? '#f44336' : avg > 8 ? '#FFC107' : '#4CAF50';
    } else {
      this.blockTimeEl.textContent = '--';
    }

    this.lastBlockEl.textContent = PPCLoaderDebugWidget.lastBlockTime > 0 ? `${PPCLoaderDebugWidget.lastBlockTime.toFixed(1)}ms` : '--';
    this.lastBlockEl.style.color = PPCLoaderDebugWidget.lastBlockTime > 16 ? '#f44336' : PPCLoaderDebugWidget.lastBlockTime > 8 ? '#FFC107' : '#4CAF50';

    this.maxBlockEl.textContent = PPCLoaderDebugWidget.maxBlockTime > 0 ? `${PPCLoaderDebugWidget.maxBlockTime.toFixed(1)}ms` : '--';
    this.maxBlockEl.style.color = PPCLoaderDebugWidget.maxBlockTime > 16 ? '#f44336' : PPCLoaderDebugWidget.maxBlockTime > 8 ? '#FFC107' : '#4CAF50';

    // Record frame event
    PPCLoaderDebugWidget.frameEvents.push({
      timestamp: performance.now(),
      workers: PPCLoaderDebugWidget.activeWorkers,
      callbacks: PPCLoaderDebugWidget.workerCallbacksThisFrame,
      blockTime: PPCLoaderDebugWidget.lastBlockTime,
    });

    if (PPCLoaderDebugWidget.frameEvents.length > PPCLoaderDebugWidget.maxFrameHistory) {
      PPCLoaderDebugWidget.frameEvents.shift();
    }

    // Reset frame counters
    PPCLoaderDebugWidget.workerCallbacksThisFrame = 0;

    // Update hierarchy stats
    const hierarchyQueue = (window as unknown as { hierarchyLoadQueue?: { getQueueSize?: () => number } }).hierarchyLoadQueue;
    const queueSize = hierarchyQueue?.getQueueSize?.() ?? 0;
    this.hierarchyQueueEl.textContent = queueSize.toLocaleString();
    this.hierarchyQueueEl.style.color = queueSize > 10000 ? '#f44336' : queueSize > 1000 ? '#FFC107' : '#00BCD4';

    this.hierarchyTimeEl.textContent = PPCLoaderDebugWidget.maxHierarchyTime > 0 ? `${PPCLoaderDebugWidget.maxHierarchyTime.toFixed(1)}ms` : '--';
    this.hierarchyTimeEl.style.color = PPCLoaderDebugWidget.maxHierarchyTime > 8 ? '#f44336' : PPCLoaderDebugWidget.maxHierarchyTime > 4 ? '#FFC107' : '#00BCD4';

    PPCLoaderDebugWidget.hierarchyLoadsThisFrame = 0;

    // Update tile operation stats
    if (PPCLoaderDebugWidget.operationTotals.size > 0) {
      const opParts: string[] = [];
      const opOrder = ['loadXYZ', 'loadColors', 'loadIntensities', 'loadClassifications', 'loadSegments', 'loadSources'];
      for (const op of opOrder) {
        const data = PPCLoaderDebugWidget.operationTotals.get(op);
        if (data && data.count > 0) {
          const avg = data.total / data.count;
          const color = avg > 50 ? '#f44336' : avg > 20 ? '#FFC107' : '#4CAF50';
          const shortName = op.replace('load', '');
          opParts.push(`<span style="color:${color}">${shortName}:${avg.toFixed(1)}</span>`);
        }
      }
      this.opStatsEl.innerHTML = opParts.join(' | ') || '--';
    } else {
      this.opStatsEl.textContent = '--';
    }

    // Show slowest tile if we have one
    if (PPCLoaderDebugWidget.lastSlowTile) {
      const tile = PPCLoaderDebugWidget.lastSlowTile;
      this.slowTileEl.style.display = 'block';
      const opBreakdown = tile.timings.map((t) => `${t.op.replace('load', '')}:${t.ms.toFixed(0)}`).join(' ');
      this.slowTileInfoEl.innerHTML = `${tile.numPoints.toLocaleString()} pts, ${tile.totalMs.toFixed(0)}ms (${opBreakdown})`;
    } else {
      this.slowTileEl.style.display = 'none';
    }

    // Draw timeline
    this.drawTimeline();
  }

  private drawTimeline(): void {
    const { width, height } = this.canvas;
    const ctx = this.ctx;

    // Clear
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.fillRect(0, 0, width, height);

    if (PPCLoaderDebugWidget.frameEvents.length < 2) return;

    const events = PPCLoaderDebugWidget.frameEvents;
    const maxWorkers = Math.max(...events.map((e) => e.workers), 1);
    const maxCallbacks = Math.max(...events.map((e) => e.callbacks), 1);

    const step = width / PPCLoaderDebugWidget.maxFrameHistory;

    // Draw worker activity (blue bars)
    ctx.fillStyle = 'rgba(33, 150, 243, 0.6)';
    events.forEach((event, i) => {
      const x = i * step;
      const barHeight = (event.workers / maxWorkers) * (height / 2);
      ctx.fillRect(x, height / 2 - barHeight, step - 1, barHeight);
    });

    // Draw callback spikes (red bars)
    ctx.fillStyle = 'rgba(244, 67, 54, 0.8)';
    events.forEach((event, i) => {
      const x = i * step;
      const barHeight = (event.callbacks / Math.max(maxCallbacks, 5)) * (height / 2);
      ctx.fillRect(x, height / 2, step - 1, barHeight);
    });

    // Draw center line
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
    ctx.beginPath();
    ctx.moveTo(0, height / 2);
    ctx.lineTo(width, height / 2);
    ctx.stroke();

    // Draw labels
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.font = '9px monospace';
    ctx.fillText('Workers', 5, 12);
    ctx.fillText('Callbacks', 5, height - 5);
  }

  destroy(): void {
    // Cleanup if needed
  }

  // Static methods for tracking from other modules
  static trackWorkerStart(): void {
    PPCLoaderDebugWidget.activeWorkers++;
  }

  static trackWorkerEnd(): void {
    PPCLoaderDebugWidget.activeWorkers = Math.max(0, PPCLoaderDebugWidget.activeWorkers - 1);
  }

  static trackTileLoadStart(): void {
    PPCLoaderDebugWidget.tilesLoading++;
  }

  /** A tile load was abandoned (node disposed/evicted mid-flight) — it is no longer loading, but never loaded. */
  static trackTileLoadAbort(): void {
    PPCLoaderDebugWidget.tilesLoading = Math.max(0, PPCLoaderDebugWidget.tilesLoading - 1);
  }

  static trackTileLoadEnd(loadTimeMs: number): void {
    PPCLoaderDebugWidget.tilesLoading = Math.max(0, PPCLoaderDebugWidget.tilesLoading - 1);
    PPCLoaderDebugWidget.tilesLoaded++;
    PPCLoaderDebugWidget.loadTimes.push(loadTimeMs);

    if (PPCLoaderDebugWidget.loadTimes.length > PPCLoaderDebugWidget.maxLoadTimes) {
      PPCLoaderDebugWidget.loadTimes.shift();
    }
  }

  static trackWorkerCallback(): void {
    PPCLoaderDebugWidget.workerCallbacksThisFrame++;
  }

  static trackBlockTime(blockTimeMs: number): void {
    PPCLoaderDebugWidget.lastBlockTime = blockTimeMs;
    PPCLoaderDebugWidget.blockTimes.push(blockTimeMs);

    if (blockTimeMs > PPCLoaderDebugWidget.maxBlockTime) {
      PPCLoaderDebugWidget.maxBlockTime = blockTimeMs;
    }

    if (PPCLoaderDebugWidget.blockTimes.length > PPCLoaderDebugWidget.maxBlockTimes) {
      PPCLoaderDebugWidget.blockTimes.shift();
    }
  }

  /**
   * Track hierarchy loading time.
   * Call this from PPCPointCloudGeometryNode after loadHierarchy().
   */
  static trackHierarchyLoad(timeMs: number): void {
    PPCLoaderDebugWidget.hierarchyLoadsThisFrame++;
    PPCLoaderDebugWidget.lastHierarchyTime = timeMs;
    PPCLoaderDebugWidget.hierarchyLoadTimes.push(timeMs);

    if (timeMs > PPCLoaderDebugWidget.maxHierarchyTime) {
      PPCLoaderDebugWidget.maxHierarchyTime = timeMs;
    }

    if (PPCLoaderDebugWidget.hierarchyLoadTimes.length > 100) {
      PPCLoaderDebugWidget.hierarchyLoadTimes.shift();
    }
  }

  /**
   * Track per-operation timing for a tile load.
   * Call this from PPCDataTile after processing geometry.
   */
  static trackTileOperations(entry: TileTimingEntry): void {
    // Store tile timing
    PPCLoaderDebugWidget.tileTimings.push(entry);
    if (PPCLoaderDebugWidget.tileTimings.length > PPCLoaderDebugWidget.maxTileTimings) {
      PPCLoaderDebugWidget.tileTimings.shift();
    }

    // Track slowest tile (only if > 100ms)
    if (entry.totalMs > 100) {
      if (!PPCLoaderDebugWidget.lastSlowTile || entry.totalMs > PPCLoaderDebugWidget.lastSlowTile.totalMs) {
        PPCLoaderDebugWidget.lastSlowTile = entry;
      }
    }

    // Aggregate operation totals for averages
    for (const timing of entry.timings) {
      const existing = PPCLoaderDebugWidget.operationTotals.get(timing.op);
      if (existing) {
        existing.total += timing.ms;
        existing.count++;
      } else {
        PPCLoaderDebugWidget.operationTotals.set(timing.op, { total: timing.ms, count: 1 });
      }
    }
  }

  static reset(): void {
    PPCLoaderDebugWidget.activeWorkers = 0;
    PPCLoaderDebugWidget.tilesLoading = 0;
    PPCLoaderDebugWidget.tilesLoaded = 0;
    PPCLoaderDebugWidget.loadTimes = [];
    PPCLoaderDebugWidget.workerCallbacksThisFrame = 0;
    PPCLoaderDebugWidget.frameEvents = [];
    PPCLoaderDebugWidget.blockTimes = [];
    PPCLoaderDebugWidget.lastBlockTime = 0;
    PPCLoaderDebugWidget.maxBlockTime = 0;
    PPCLoaderDebugWidget.tileTimings = [];
    PPCLoaderDebugWidget.lastSlowTile = null;
    PPCLoaderDebugWidget.operationTotals = new Map();
    PPCLoaderDebugWidget.hierarchyLoadTimes = [];
    PPCLoaderDebugWidget.lastHierarchyTime = 0;
    PPCLoaderDebugWidget.maxHierarchyTime = 0;
    PPCLoaderDebugWidget.hierarchyLoadsThisFrame = 0;
  }
}

// Expose globally for tracking from other modules
if (typeof window !== 'undefined') {
  window.PPCLoaderDebugWidget = PPCLoaderDebugWidget;
}
